import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Divider } from '@mui/material';
import { toast } from '../../../../../../../ui/toast';
import { Button, Drawer, ListItemButton } from '../../../../../../../ui';
import { addQuestionToAssessment } from '../../../../../../../utils/api_service/assessment_api';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import {
  Question,
  QuestionSearch,
  SubSkill,
} from '../../../../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../../../../utils/types/react_select_types';
import { Filter } from './Components';
import { useSelectQuestions } from './Hooks';

interface ISelectQuestions {
  isOpen: boolean;
  onCloseDrawer: () => void;
  onSave: (finalSelection: Question[]) => void;
  assessmentId: string;
  skillList: SelectOptions[] | undefined;
  levelList: SelectOptions[] | undefined;
  subSkillList: SubSkill[] | undefined;
  jobTitleList: SelectOptions[] | undefined;
  initialSelected: Question[];
  onAddQuestion: (q: Question) => void;
}

export const SelectQuestions: React.FC<ISelectQuestions> = ({
  isOpen,
  onCloseDrawer,
  onSave,
  assessmentId,
  skillList,
  levelList,
  subSkillList,
  jobTitleList,
  initialSelected,
  onAddQuestion,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const SQ = ADMIN_PAGES_KEYS.jobOpenings.selectQuestions;
  const [searchObject, setSearchObject] = useState<QuestionSearch>({
    questionTitle: '',
    skillId: '',
    levelId: '',
    subSkill1Id: '',
    subSkill2Id: '',
    subSkill3Id: '',
    jobTitle: '',
    assessmentId,
  });

  const {
    questionsList,
    selectedQuestions,
    loading,
    totalCount,
    pageNumber,
    totalPages,
    handleSelect,
    handleRemove,
    handleSelectAll,
    handleRemoveAll,
    goToNextPage,
    goToPrevPage,
    setIsSaving,
    isSaving,
  } = useSelectQuestions({ searchObject, initialSelected });

  const handleSaveClick = async () => {
    setIsSaving(true);

    const toAdd = selectedQuestions.filter(
      (q) => !initialSelected.some((s) => s.id === q.id),
    );

    try {
      await Promise.all(
        toAdd.map((q) =>
          addQuestionToAssessment('', q.id, assessmentId),
        ),
      );
      toast.success('Questions added successfully!');
      onSave(selectedQuestions);
    } catch {
      toast.error('Failed to add some questions.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onCloseDrawer}
      width="w-full md:w-[800px]"
      title={t(SQ.drawerTitle)}
      footerContent={
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'flex-end',
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          <Button
            label={t(SQ.cancel)}
            onClick={onCloseDrawer}
            variant="secondary"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          />
          <Button
            label={isSaving ? t(SQ.saving) : t(SQ.save)}
            onClick={handleSaveClick}
            variant="primary"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          />
        </Box>
      }
    >
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          minHeight: '70vh',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: { xs: 2, lg: 3 },
        }}
      >
        {/* Filter pane */}
        <Box sx={{ width: { xs: '100%', lg: 250 }, flexShrink: 0 }}>
          <Filter
            skillList={skillList || []}
            levelList={levelList || []}
            subSkillList={subSkillList}
            jobTitleList={jobTitleList}
            searchObject={searchObject}
            setSearchObject={setSearchObject}
          />
        </Box>

        {/* Divider */}
        <Divider
          orientation="vertical"
          flexItem
          sx={{ display: { xs: 'none', lg: 'block' }, borderStyle: 'dashed' }}
        />

        {/* Main content: top and bottom halves */}
        <Box sx={{ display: 'flex', minHeight: 0, flex: 1, flexDirection: 'column' }}>
          {/* Top half: Available Questions */}
          <Box sx={{ minHeight: 260, display: 'flex', flexDirection: 'column', height: { lg: '50%' } }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t(SQ.allQuestions)}
              </Typography>
              <Button
                variant="link"
                label={t(SQ.selectAll)}
                onClick={() => {
                  handleSelectAll();
                  questionsList.forEach(onAddQuestion);
                }}
                disabled={questionsList.length === 0}
              />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t(SQ.pageOf, { page: pageNumber, total: totalPages, count: totalCount })}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  label={t(SQ.previous)}
                  onClick={goToPrevPage}
                  variant="secondary"
                  disabled={pageNumber === 1 || loading}
                />
                <Button
                  label={t(SQ.next)}
                  onClick={goToNextPage}
                  variant="secondary"
                  disabled={pageNumber === totalPages || loading}
                />
              </Box>
            </Box>
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                border: 1,
                borderColor: 'divider',
                p: 1,
                borderRadius: 1.5,
              }}
            >
              {loading ? (
                <Typography variant="body2" color="text.secondary">
                  {t(SQ.loading)}
                </Typography>
              ) : questionsList.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t(SQ.noQuestions)}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {questionsList.map((q) => (
                    <ListItemButton
                      key={q.id}
                      onClick={() => {
                        handleSelect(q);
                        onAddQuestion(q);
                      }}
                    >
                      {q.question || t(SQ.untitled)}
                    </ListItemButton>
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          {/* Divider line */}
          <Divider sx={{ my: 1.5 }} />

          {/* Bottom half: Selected Questions */}
          <Box sx={{ minHeight: 260, display: 'flex', flexDirection: 'column', height: { lg: '50%' } }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t(SQ.selectedQuestions)}
              </Typography>
              <Button
                variant="link"
                label={t(SQ.removeAll)}
                onClick={handleRemoveAll}
                disabled={selectedQuestions.length === 0}
              />
            </Box>
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                border: 1,
                borderColor: 'divider',
                p: 1,
                borderRadius: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              {selectedQuestions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t(SQ.noSelected)}
                </Typography>
              ) : (
                selectedQuestions.map((q) => (
                  <ListItemButton
                    key={q.id}
                    isSelected
                    onClick={() => {}}
                    onRemove={() => handleRemove(q)}
                  >
                    {q.question || t(SQ.untitled)}
                  </ListItemButton>
                ))
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default SelectQuestions;
